import { tileDataProp } from 'components/atoms/TileButton';
import Barchart from 'components/molecules/Barchart';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';
import Loader from 'components/molecules/Loader';
import { salesDataType, salesTileItemType } from 'components/pages/GrowthAnalysis';
import { growthAnalysisTableId, height, yAxisLabelClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import { Tooltip } from 'antd';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import React, { forwardRef, useContext, useEffect, useMemo } from 'react';
import {
    calculateFontSize,
    fetchCurrency,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    formatUnit,
    scaleMaxValue,
    scaleMinValue,
    ellipseMiddleString,
    getConfig,
} from 'utils/helperMethods';
import { barWidth, yAxisLabelMapping } from '../SalesChartContainer/SalesChartContainer';
import { UserContext } from 'contexts/ConfigContext';

interface Props {
    index: number;
    data: salesDataType;
    salesTile: salesTileItemType;
    absTile: tileDataProp;
    volTile: tileDataProp;
    infiniteScrollerNext: (index: number, width: number) => void;
    infiniteScrollSettings: any;
    showGroupedChartLabel: boolean;
}

const yAxisGraphWidth = calculateFontSize(window.innerWidth, 60);
export const groupBarChartWidth = calculateFontSize(window.innerWidth, 100);
export const graphXAxisLabelFontSize = calculateFontSize(window.innerWidth, 13);

const BrandValueChartContainer = forwardRef<HTMLDivElement, Props>((props, ref) => {
    const {
        index,
        data,
        salesTile,
        absTile,
        volTile,
        infiniteScrollerNext,
        infiniteScrollSettings,
        showGroupedChartLabel,
    } = props;
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const showFullLabel = commonConfig?.showFullLabel;
    const decimalForPercentage = commonConfig?.decimalForPercentage ?? 2;
    const decimalForNonPercentage = commonConfig?.decimalForNonPercentage ?? 2;
    const growthAnalysisConfig = getConfig('growthAnalysis', appConfig);
    const showTotal = growthAnalysisConfig?.showTotal;

    const containerWidth = useContainerWidth({
        containerId: '', //giving an id that doesnt exists so that it calculates to default container width
        buffer: yAxisGraphWidth + 34 * 2 + 60,
    }); //subtracting graph width and padding on 2 sides
    const { chartData: salesData, loading, error, count } = data;
    const { width = [0, 0] } = infiniteScrollSettings;
    const currency = volTile?.key === 'val' && absTile.key === 'abs' ? fetchCurrency(appConfig) : '';

    //graph width ( includes total graph + other data points as well)
    const graphViewContainerWidth = Math.max(
        salesData?.data ? (salesData.data?.length / 2) * groupBarChartWidth : 0,
        containerWidth,
    );
    //width of each bar chart
    const perGraphWidth = graphViewContainerWidth / (salesData?.data?.length / 2);
    const infiniteScrollOnNext = (width: number, loading: boolean) => {
        if (loading) return;
        //  console.log('logging asd', width, loading);
        infiniteScrollerNext(index, width);
    };
    const legendData = {
        incremental: [
            {
                label: volTile.key === 'profit' ? `Base Profit` : `Base ${volTile.label} Sales`,
                color: colorConstants.orange,
            },
            {
                label: volTile.key === 'profit' ? `Incremental Profit` : `Incremental ${volTile.label} Sales`,
                color: colorConstants.darkBlue,
            },
        ],
        promo: [
            {
                label: ['units', 'profit'].includes(String(volTile.key)) ? `Non-Promo ${volTile.label}` : 'Non-promo',
                color: colorConstants.orange,
            },
            {
                label: ['units', 'profit'].includes(String(volTile.key)) ? `Promo ${volTile.label}` : 'Promo',
                color: colorConstants.darkBlue,
            },
        ],
        sales: [],
        depletion: [],
    };
    //listening to scroll
    useEffect(() => {
        //table element
        const tableId = `${growthAnalysisTableId}-${index}`;
        const tableScrollableElement = document.getElementById(tableId)?.getElementsByClassName('ant-table-body')[0];
        //graph element
        const graphElem = document.getElementById(`groupedChartView`);

        //scroll table when user is scrolling grouped bar chart
        function scrollFunction(event: any) {
            tableScrollableElement?.scrollTo({
                left: Number(graphElem?.scrollLeft) * 1, //giving a buffer because widths of table and chart are different
                behavior: 'auto',
            });
        }
        if (salesData?.data?.length > 0) {
            graphElem?.addEventListener('scroll', scrollFunction, { passive: true });
            return () => {
                graphElem?.scrollTo({
                    left: 0,
                });
                graphElem?.removeEventListener('scroll', scrollFunction);
            };
        }
    }, [salesData]);

    const memoisedSalesData = useMemo(() => {
        let totalData: any[] = []; //for total data
        let dataExcludingTotal: any[] = [...(salesData?.data ?? [])]; //for data excluding total
        const middleIndex = Math.floor(salesData?.data.length / 2);
        if (showTotal && salesData?.data && Array.isArray(salesData?.data)) {
            //adding total data points
            totalData = [salesData?.data[0], salesData?.data[middleIndex]];

            //removing the total data points
            dataExcludingTotal.splice(middleIndex, 1);
            dataExcludingTotal.splice(0, 1);
        }
        return {
            totalData,
            dataExcludingTotal,
        };
    }, [salesData]);

    const YAxisUnit: any = formatUnit(`${absTile.key}_${volTile.key}`, true, appConfig);
    let YAxisResult: any =
        volTile.key === 'vol' && absTile.key === 'percentage'
            ? `Eq Vol ${YAxisUnit}`
            : `${yAxisLabelMapping[volTile.key]} ${YAxisUnit}`;

    // IF ERROR RETURN ERROR MESSAGE
    if (error && !loading && !salesData) return <ErrorBox msg={String(error)} />;

    // IF LOADING RETURN LOADER
    if (loading)
        return (
            <div style={{ height, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );

    // ELSE RETURN COMPONENT
    return (
        <section style={{ minHeight: height }} ref={ref}>
            <div className="mb-4 flex justify-between items-center">
                <p className={`${yAxisLabelClass} pl-[40px] w-[220px]`}> Change in {YAxisResult}</p>
                <Legend data={legendData[salesTile.key]} />
                <p className="w-[120px] font-normal text-greyFont text-[11px]">
                    {absTile.key === 'percentage' ? '* % scale in Y-axis from -100% to +200%' : ''}
                </p>
            </div>
            <div className="flex ml-1 w-full">
                <div style={{ marginLeft: 60, width: yAxisGraphWidth }}>
                    <Barchart
                        // padding={[0, 0, 0, 48]}
                        padding={[0, 0, 0, 0]}
                        barWidth={barWidth}
                        xAxisConfig={false}
                        dataset={[]}
                        height={height}
                        loading={false}
                        chartContainer={`${salesTile.key}Y-${index}`}
                        fields={['category', 'sales']}
                        minBound={scaleMinValue(60, salesData?.minVal)}
                        maxBound={scaleMaxValue(30, salesData?.maxVal)}
                        width={yAxisGraphWidth}
                        colorKey={'type'}
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMB(val);
                        }}
                        // Yline
                        YGrid={false}
                    />
                </div>
                {/* TOTAL CHART */}
                {/* make the total bar graph sticky */}
                {showTotal && (
                    <div>
                        <div style={{ minHeight: height }}>
                            <Barchart
                                intervalLabelTooltipKey={'labelSales'}
                                barWidth={barWidth}
                                disableIntervalLabel={!showGroupedChartLabel}
                                // yAxisConfig={false}
                                dataset={memoisedSalesData?.totalData || []}
                                height={height}
                                //  showLegend
                                loading={false}
                                chartContainer={`${salesTile.key}X-total-${index}`}
                                fields={['category', 'sales', 'type']}
                                minBound={scaleMinValue(60, salesData?.minVal)}
                                maxBound={scaleMaxValue(30, salesData?.maxVal)}
                                width={perGraphWidth} //total 1st element
                                // xAxisLabelFormatter={(val: any) => {
                                //     return addLineAndFormatString(
                                //         val,
                                //         Math.floor((perGraphWidth || groupBarChartWidth) / (groupBarChartWidth / 16)), //got 4.5 when 73 default width is divided by 16 character
                                //     );
                                // }}
                                intervalFormatter={(val) => {
                                    return absTile.key === 'percentage'
                                        ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                        : currency +
                                              formatNumWithKMBAlt(
                                                  val,
                                                  decimalForNonPercentage,
                                                  decimalForNonPercentage,
                                              );
                                }}
                                tooltipFormatter={(xAxis, val) => {
                                    return {
                                        name: xAxis,
                                        value:
                                            absTile.key === 'percentage'
                                                ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                                : currency +
                                                  formatNumWithKMBAlt(
                                                      val,
                                                      decimalForNonPercentage,
                                                      decimalForNonPercentage,
                                                  ),
                                    };
                                }}
                                //  addPercentageInIntervalLabel={absTile.key === 'percentage'}
                                colorKey={'type'}
                                enableGroupedChart
                                groupedChartColor={[colorConstants.orange, colorConstants.darkBlue]}
                            />
                        </div>
                        {/* total label */}
                        <div
                            style={{
                                width: perGraphWidth,
                                minWidth: perGraphWidth,
                                overflowWrap: 'anywhere',
                                fontSize: graphXAxisLabelFontSize,
                            }}
                            className="text-center p-[2px]"
                        >
                            {memoisedSalesData.totalData?.[0]?.category}
                        </div>
                    </div>
                )}

                <HorizontalInfiniteScroller
                    id={`groupedChartView`}
                    hasMore={salesData?.data?.length / 2 < count || false} //dividing by 2 to remove grouped values
                    scrollThreshold={0.9}
                    toScrollWidth={width[index]}
                    onNext={(width: number) => {
                        infiniteScrollOnNext(width, loading);
                    }}
                >
                    <div id="groupedChartView" style={{ paddingBottom: 45, overflowX: 'auto', maxWidth: '100%' }}>
                        <div className="flex" style={{ minHeight: height }}>
                            {/* EXCLUDING TOTAL */}

                            <Barchart
                                intervalLabelTooltipKey={'labelSales'}
                                barWidth={barWidth}
                                // yAxisConfig={false}
                                dataset={memoisedSalesData?.dataExcludingTotal || []}
                                height={height}
                                //  showLegend
                                disableIntervalLabel={!showGroupedChartLabel}
                                loading={false}
                                chartContainer={`${salesTile.key}X-${index}`}
                                fields={['category', 'sales', 'type']}
                                minBound={scaleMinValue(60, salesData?.minVal)}
                                maxBound={scaleMaxValue(30, salesData?.maxVal)}
                                width={Math.min(
                                    perGraphWidth * (memoisedSalesData?.dataExcludingTotal.length / 2) || // width of graph excluding total
                                        window.innerWidth,
                                    32767,
                                )} //here 32767 is the max width of the canvas in browser
                                // xAxisLabelFormatter={(val: any) => {
                                //     return addLineAndFormatString(
                                //         val,
                                //         Math.floor((perGraphWidth || groupBarChartWidth) / (groupBarChartWidth / 16)), //got 4.5 when 73 default width is divided by 16 character
                                //     );
                                // }}
                                intervalFormatter={(val) => {
                                    return absTile.key === 'percentage'
                                        ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                        : currency +
                                              formatNumWithKMBAlt(
                                                  val,
                                                  decimalForNonPercentage,
                                                  decimalForNonPercentage,
                                              );
                                }}
                                tooltipFormatter={(xAxis, val) => {
                                    return {
                                        name: xAxis,
                                        value:
                                            absTile.key === 'percentage'
                                                ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                                : currency +
                                                  formatNumWithKMBAlt(
                                                      val,
                                                      decimalForNonPercentage,
                                                      decimalForNonPercentage,
                                                  ),
                                    };
                                }}
                                //  addPercentageInIntervalLabel={absTile.key === 'percentage'}
                                colorKey={'type'}
                                enableGroupedChart
                                groupedChartColor={[colorConstants.orange, colorConstants.darkBlue]}
                            />
                        </div>
                        <div className="flex">
                            {/* rest of data label */}
                            {memoisedSalesData?.dataExcludingTotal
                                ?.slice?.(0, memoisedSalesData?.dataExcludingTotal?.length / 2)
                                ?.map((item: any, idx: number) => {
                                    return (
                                        <div
                                            style={{
                                                width: perGraphWidth,
                                                minWidth: perGraphWidth,
                                                overflowWrap: 'anywhere',
                                                fontSize: graphXAxisLabelFontSize,
                                            }}
                                            className="text-center p-[2px]"
                                            key={item?.category}
                                        >
                                            {showFullLabel ? (
                                                item?.category
                                            ) : (
                                                <Tooltip
                                                    placement="top"
                                                    color="white"
                                                    destroyTooltipOnHide
                                                    overlayStyle={{ borderRadius: 4 }}
                                                    overlayInnerStyle={{
                                                        fontSize: graphXAxisLabelFontSize,
                                                        color: colorConstants.darkGrey,
                                                    }}
                                                    title={item?.category}
                                                >
                                                    <div>{ellipseMiddleString(item?.category, 8, 3)}</div>
                                                </Tooltip>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </HorizontalInfiniteScroller>
            </div>
        </section>
    );
});

export default BrandValueChartContainer;
